import { useEffect, useRef, useState } from 'react';

import { useAtom, useSetAtom } from 'jotai';
import { usePathname } from 'next/navigation';

import type { MakeModelAtom } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import { ChevronDown } from '@gds/Icons/Paths/ChevronDown';
import { areaAtom } from '@search/Atoms/AreaAtom';
import { regionAtom } from '@search/Atoms/RegionAtom';
import type { RegionAreaAtom } from '@search/Atoms/RegionAtom.entity';
import { shouldShowRegionLoaderAtom } from '@search/Atoms/RegionLoadingStateAtom';

import { locationHubPostcodeAtom } from '../../Atoms/PostcodeAtom/LocationHubPostcodeAtom';
import type { RegionAreaProps } from '../../Search.entity';
import CustomDropDown from '../CustomDropDown/CustomDropDown';

import styles from '../../Search.module.css';

const Region = ({
  regionPlaceholder,
  areaPlaceholder,
  searchPlaceholder,
  noResultText,
  regionTitle,
  state,
}: RegionAreaProps) => {
  const DEFAULT_OPTION = {
    key: 'any_region',
    displayName: regionPlaceholder,
    region: 'anyregion',
    location: {
      lat: 0,
      lon: 0,
    },
    postcode: '',
  };
  const regionRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredRegions, setFilteredRegions] = useState<RegionAreaAtom[]>([]);
  const [selectedRegion, setSelectedRegion] = useAtom(regionAtom);
  const setLocationHubPostcode = useSetAtom(locationHubPostcodeAtom);

  const setSelectedArea = useSetAtom(areaAtom);
  const [shouldShowLoader, setShouldShowLoader] = useAtom(shouldShowRegionLoaderAtom);
  const pathname = usePathname();

  const handleMenuOpen = () => {
    setIsOpen(isOpen => !isOpen);
    setSearchKeyword('');
  };

  const eligibleRegions = state.regions;

  useEffect(() => {
    if (searchKeyword) {
      const filteredArr = eligibleRegions?.filter(data => {
        return data.displayName.toLowerCase().includes(searchKeyword);
      });
      setFilteredRegions(searchKeyword ? filteredArr : []);
    }
  }, [searchKeyword]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!regionRef?.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
  }, [regionRef]);

  useEffect(() => {
    if (shouldShowLoader) {
      setShouldShowLoader(false);
    }
  }, [pathname]);

  const handleSearchChange = (value: string) => {
    setSearchKeyword(value.toLowerCase());
  };

  const handleMenuClose = (option: RegionAreaAtom) => {
    if (option.key === selectedRegion.key) {
      return;
    }
    if (option) {
      const regionLat = option.location.lat;
      const regionLon = option.location.lon;
      setSelectedRegion({
        ...option,
      });
      setLocationHubPostcode({
        postcode: option.postcode,
        location: { lat: regionLat, lon: regionLon },
        isValid: true,
        error: false,
      });
      setSelectedArea({
        key: 'any_area',
        displayName: areaPlaceholder,
        region: 'anyarea',
        location: DEFAULT_OPTION.location,
        postcode: DEFAULT_OPTION.postcode,
        area: 'any_area',
      });
      setIsOpen(false);
      setShouldShowLoader(true);
    }
  };

  return (
    <div
      className={`${styles.makeModelWrapper} ${isOpen ? styles.active : ''}`}
      ref={regionRef}
      data-test-id="region-component"
      aria-label="region-value"
    >
      <div
        className={`${styles.dropdownWrapper} ${styles.first}`}
        onClick={handleMenuOpen}
        data-test-id="region-menu-button"
      >
        <span className={styles.makeValue} data-test-id="region-value">
          {selectedRegion?.key === 'any_region' ? regionPlaceholder : selectedRegion?.displayName}
        </span>
        <div className={styles.iconWrapper}>
          <ChevronDown className={isOpen ? styles.rotate : ''} />
        </div>
      </div>
      {isOpen && (
        <CustomDropDown
          items={
            searchKeyword.length > 0
              ? filteredRegions
              : ([DEFAULT_OPTION, ...(eligibleRegions ?? [])] as RegionAreaAtom[])
          }
          isGrouped
          onSelect={(option: MakeModelAtom | RegionAreaAtom) =>
            handleMenuClose(option as RegionAreaAtom)
          }
          checkedValue={selectedRegion?.region}
          dataTestId="region-menu"
          searchPlaceholder={searchPlaceholder}
          handleSearchChange={handleSearchChange}
          noResultText={noResultText}
          title={regionTitle}
          handleMenuOpen={handleMenuOpen}
          dropdownType="region"
        />
      )}
    </div>
  );
};

export default Region;
